import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCog, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { US_STATES } from "@/data/pillars";
import { useAuth } from "@/context/AuthContext";

const CREDS = ["RN", "LPN", "CNA", "HHA", "OTHER"];
const RADII = [
    { value: 10, label: "10 mi" },
    { value: 25, label: "25 mi" },
    { value: 50, label: "50 mi" },
    { value: 100, label: "100 mi" },
    { value: 9999, label: "Nationwide" },
];

/**
 * Caregiver self-service profile editor. Lives inside /change-password
 * (which serves as the general "Account Settings" page).
 */
export default function CaregiverProfileCard() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/caregiver/profile");
            setForm({
                full_name: r.data.full_name || "",
                phone: r.data.phone || "",
                credentials: r.data.credentials || (r.data.credential ? [r.data.credential] : []),
                state: r.data.state || "",
                home_zip: r.data.home_zip || "",
                willing_zips: (r.data.willing_zips || []).join(", "),
                willing_radius_miles: r.data.willing_radius_miles || 25,
                on_call: !!r.data.on_call,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load profile");
        }
    }, []);

    useEffect(() => {
        if (user?.role === "caregiver") load();
    }, [user, load]);

    // Only render for caregivers
    if (user?.role !== "caregiver") return null;
    if (!form) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleCred = (c) => setForm((f) => ({
        ...f,
        credentials: f.credentials.includes(c)
            ? f.credentials.filter((x) => x !== c)
            : [...f.credentials, c],
    }));

    async function submit(e) {
        e.preventDefault();
        if (!form.credentials.length) return toast.error("At least one credential is required");
        setSaving(true);
        try {
            const willing_zips = (form.willing_zips || "")
                .split(/[,\s]+/)
                .map((z) => z.trim())
                .filter((z) => /^\d{5}$/.test(z));
            await api.patch("/caregiver/profile", {
                full_name: form.full_name,
                phone: form.phone,
                credentials: form.credentials,
                state: form.state,
                home_zip: form.home_zip,
                willing_zips,
                willing_radius_miles: form.willing_radius_miles,
                on_call: form.on_call,
            });
            toast.success("Profile updated");
            refresh?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    }

    return (
        <Card className="rounded-2xl border-stone-200 mt-8" data-testid="caregiver-profile-card">
            <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-4">
                    <UserCog className="w-5 h-5 text-emerald-700" />
                    <h2 className="font-outfit text-xl font-semibold text-stone-900">Caregiver profile</h2>
                </div>
                <p className="text-sm text-stone-600 font-dmsans mb-5">
                    Update your credentials, coverage area, and availability. Changes take effect for the next shift-blast email you'd receive.
                </p>

                <form onSubmit={submit} className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label>Full name</Label>
                            <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="prof-full-name" className="h-11 rounded-xl mt-1" />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="prof-phone" className="h-11 rounded-xl mt-1" />
                        </div>
                    </div>

                    <div>
                        <Label>Credentials <span className="text-stone-500 font-normal">(pick one or more)</span></Label>
                        <div className="mt-1 grid grid-cols-5 gap-2">
                            {CREDS.map((c) => {
                                const active = form.credentials.includes(c);
                                return (
                                    <button key={c} type="button" data-testid={`prof-cred-${c.toLowerCase()}`} data-active={active} onClick={() => toggleCred(c)}
                                        className="h-11 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-sm">{c}</button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[1fr,140px]">
                        <div>
                            <Label>Working state</Label>
                            <select value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="prof-state" className="mt-1 h-11 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-dmsans">
                                <option value="">Choose…</option>
                                {US_STATES.map(([code, name]) => <option key={code} value={code}>{name} ({code})</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Home ZIP</Label>
                            <Input value={form.home_zip} onChange={(e) => set("home_zip", e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} inputMode="numeric" maxLength={5} data-testid="prof-home-zip" className="h-11 rounded-xl mt-1" />
                        </div>
                    </div>

                    <div>
                        <Label>Other ZIPs you'll cover <span className="text-stone-500 font-normal">(optional)</span></Label>
                        <Input value={form.willing_zips} onChange={(e) => set("willing_zips", e.target.value)} placeholder="90211, 90212, 91001" data-testid="prof-willing-zips" className="h-11 rounded-xl mt-1" />
                    </div>

                    <div>
                        <Label>Distance you'll travel from Home ZIP</Label>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {RADII.map((r) => {
                                const active = form.willing_radius_miles === r.value;
                                return (
                                    <button key={r.value} type="button" data-testid={`prof-radius-${r.value}`} data-active={active} onClick={() => set("willing_radius_miles", r.value)}
                                        className="h-10 px-4 rounded-full border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-sm">{r.label}</button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="prof-oncall-wrap">
                            <input type="checkbox" checked={form.on_call} onChange={(e) => set("on_call", e.target.checked)} data-testid="prof-oncall" />
                            <span className="text-sm font-dmsans text-stone-800">Currently on-call — send me shift-blast emails now</span>
                        </label>
                    </div>

                    <div className="pt-2">
                        <Button type="submit" disabled={saving} data-testid="prof-save-btn" className="rounded-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-outfit">
                            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
